import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCheckList } from "../../../hooks/useCheckList";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { tableColumnTypes } from "../../../utils/tableColumnTypes";
import { sortTypes } from "../../../utils/tableSortTypes";
import Input from "../Input/Input";
import { FormProvider, useForm } from "react-hook-form";
import SortingIcon from "../SvgIcons/TableIcons/SortingIcon";
import classes from "./DataTable.module.scss";
import Loader from "../Loader/Loader";
import DownloadIconSmall from "../SvgIcons/CommonIcons/DownloadIconSmall";
const DataTable = ({
  columns,
  defaultData,
  setDefaultData,
  data,
  setData,
  totalRows,
  loading,
  uniqueFieldName,
  selectableRows,
  fetchMoreData,
  searchable,
  filterable,
  onDownload,
  downloadable,
  downloadText = "Download CSV",
  placeholder,
  setSearchQuery,
  isRefresh = false,
  setIsRefresh,
}) => {
  const [hasMoreData, setHasMoreData] = useState(true);
  const [currentSort, setCurrentSort] = useState();
  const [currentSortField, setCurrentSortField] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const previousDataRef = useRef(data);
  const previousSelectedIdsRef = useRef(selectedIds);
  const methods = useForm({});
  const { checkedAll, handleCheckAll, handleCheckOne } = useCheckList(
    uniqueFieldName,
    data,
    setData,
    setDefaultData
  );
  const { ref, isFetching } = useInfiniteScroll(fetchMoreData, hasMoreData);

  useEffect(() => {
    if (filterable) {
      setHasMoreData(false);
    } else if (data?.length >= totalRows) {
      setHasMoreData(false);
    } else {
      setHasMoreData(true);
    }
  }, [data, totalRows, filterable]);

  // useEffect(() => {
  //   if (defaultData.length > 0) setCurrentSort(sortTypes.sortDafault);
  // }, [defaultData]);

  const updateDataWithSelectedIds = useCallback(() => {
    if (
      (previousDataRef.current !== data ||
        previousSelectedIdsRef.current !== selectedIds) &&
      searchable
    ) {
      // Reselect rows based on collected IDs
      const updatedData = data.map((item) => ({
        ...item,
        checked: item?.checked
          ? item?.checked
          : selectedIds.has(item[uniqueFieldName]),
      }));

      // Only update state if there are actual changes
      if (JSON.stringify(updatedData) !== JSON.stringify(data)) {
        setData(updatedData);
      }
      previousDataRef.current = data;
      previousSelectedIdsRef.current = selectedIds;
    }
  }, [data, selectedIds, setData, uniqueFieldName]);

  useEffect(() => {
    updateDataWithSelectedIds();
  }, [updateDataWithSelectedIds]);

  useEffect(() => {
    if (isRefresh) {
      setSelectedIds(new Set());
      setIsRefresh(false);
    }
  }, [isRefresh]);

  const sortTable = (sortable, fieldName, type) => {
    if (sortable) {
      let nextSort;
      if (currentSortField !== fieldName) nextSort = sortTypes.sortUp;
      else if (currentSort === sortTypes.sortDown)
        nextSort = sortTypes.sortDafault;
      else if (currentSort === sortTypes.sortUp) nextSort = sortTypes.sortDown;
      else if (currentSort === sortTypes.sortDafault)
        nextSort = sortTypes.sortUp;
      setCurrentSort(nextSort);

      const sortedData = [...data].sort((a, b) => {
        if (a[fieldName] === null) return 1;
        if (b[fieldName] === null) return -1;
        if (a[fieldName] === null && b[fieldName] === null) return 0;

        // Use custom sorting function if provided
        // if (customSortFunction) {
        //   return customSortFunction(a, b, fieldName, nextSort);
        // }

        if (type === tableColumnTypes.DATE) {
          if (nextSort === sortTypes.sortUp)
            return new Date(b[fieldName]) < new Date(a[fieldName]) ? 1 : -1;
          return new Date(b[fieldName]) > new Date(a[fieldName]) ? 1 : -1;
        }
        return (
          a[fieldName].toString().localeCompare(b[fieldName].toString(), "en", {
            numeric: true,
          }) * (nextSort === sortTypes.sortUp ? 1 : -1)
        );
      });

      if (nextSort === sortTypes.sortDafault) {
        setData(defaultData);
      } else {
        setData(sortedData);
      }
      setCurrentSortField(fieldName);
    }
  };

  const handleFilterOrSearch = (e) => {
    if (filterable) {
      if (e.target.value === "") {
        setData(defaultData);
      } else {
        const searchedData = columns.map((columns) => {
          const filteredData = defaultData.filter((row) => {
            if (
              columns.fieldName &&
              row[columns.fieldName]
                .toString()
                .toLowerCase()
                .includes(e.target.value.toString().toLowerCase())
            ) {
              return true;
            } else {
              return false;
            }
          });
          return filteredData;
        });
        setData(searchedData[0]);
      }
    } else {
      setSearchQuery(e.target.value);
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prevSelectedIds) => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (newSelectedIds.has(id)) {
        newSelectedIds.delete(id);
      } else {
        newSelectedIds.add(id);
      }
      return newSelectedIds;
    });
    handleCheckOne(id);
  };

  const handleCheckAllRows = () => {
    setSelectedIds((prevSelectedIds) => {
      const newSelectedIds = new Set();
      if (checkedAll) {
        // If all rows are currently checked, uncheck all
        data.forEach((item) => newSelectedIds.delete(item[uniqueFieldName]));
      } else {
        // If not all rows are checked, check all
        data.forEach((item) => newSelectedIds.add(item[uniqueFieldName]));
      }
      return newSelectedIds;
    });
    handleCheckAll();
  };

  return (
    <div className={classes.dataTable}>
      <div className={classes.dataTable__main} ref={ref}>
        <table className={classes.dataTable__main__table}>
          <thead>
            <tr>
              {selectableRows ? (
                <th>
                  <input
                    type={"checkbox"}
                    checked={checkedAll}
                    onChange={() => {
                      handleCheckAllRows();
                    }}
                  />
                </th>
              ) : (
                <></>
              )}
              {columns.map((column) => {
                return (
                  <th
                    key={column.ID}
                    onClick={() => {
                      setCurrentSortField(column.fieldName);
                      sortTable(column.sort, column.fieldName, column.type);
                    }}
                    style={{ cursor: column.sort ? "pointer" : "default" }}
                  >
                    {column.sort ? (
                      <div
                        className={classes.dataTable__main__table__header__th}
                      >
                        {column.name}{" "}
                        {currentSortField === column.fieldName ? (
                          <SortingIcon fill={currentSort} />
                        ) : (
                          <SortingIcon />
                        )}
                      </div>
                    ) : (
                      column.name
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data?.map((row, index) => {
              return (
                <tr key={row[uniqueFieldName]}>
                  {selectableRows ? (
                    <td>
                      <input
                        type={"checkbox"}
                        checked={row.checked ? true : false}
                        onChange={() => {
                          handleCheckboxChange(row[uniqueFieldName]);
                        }}
                      />
                    </td>
                  ) : (
                    <></>
                  )}
                  {columns.map((column) => {
                    return <td key={column.ID}>{column.value(row, index)}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {(isFetching && hasMoreData) || loading ? (
          <div className={classes.dataTable__main__loading}>
            <div className={classes.dataTable__main__loading__loader}>
              <Loader />
            </div>
          </div>
        ) : data?.length === 0 ? (
          <h1
            className={
              classes.dataTable__main__loading +
              " " +
              classes.dataTable__main__loading__noDataFoundText
            }
          >
            No Data Found
          </h1>
        ) : (
          <></>
        )}
      </div>
      <div className={classes.dataTable__footer}>
        <div>{`Showing ${data?.length} out of ${totalRows}`}</div>
        {searchable || filterable ? (
          <FormProvider {...methods}>
            <Input
              name="Search"
              placeholder={placeholder || "Search here"}
              labelColor="light"
              search={true}
              borderRadius={2.3}
              height={4}
              width={45}
              onChange={handleFilterOrSearch}
              gap={0}
            />
          </FormProvider>
        ) : downloadable ? (
          <div
            className={classes.dataTable__footer__download}
            onClick={onDownload}
          >
            <div className={classes.dataTable__footer__download__downloadIcon}>
              <DownloadIconSmall />
            </div>
            {downloadText}
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default DataTable;
